import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Account, Iban } from '@shared/contracts/entities';
import {
  allowedAccountActions,
  getAccount,
  getIbansForAccount,
  renameAccount,
  updateAccountStatus,
  type CustomerAccountAction,
} from '@/mocks/api';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { newIdempotencyKey } from '@/services/idempotency';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, DetailList, SectionHeader, StatusBadge } from '@/components/banking';
import { CardSkeleton, ErrorState, LoadingRegion, Notice } from '@/components/feedback';
import { ConfirmSheet, Sheet } from '@/components/feedback/Sheet';
import { useToast } from '@/components/feedback/Toast';
import { formatIban, formatSortCode } from '@/services/formatters';
import { CopyField } from './CopyField';

/**
 * Account detail.
 *
 * TRACE:XS-SMEM-WR-038 — available, current/ledger and reserved balances are
 * shown separately, per currency, and never merged into one figure.
 * TRACE:XS-SMEM-WR-037 — sort code and account number are the primary UK
 * domestic details; IBAN and BIC are presented as secondary.
 * TRACE:XS-SMEM-WR-041 — one consistent detail screen serves both domestic and
 * international identifiers.
 * TRACE:XS-SMEM-WR-028 — receive-only, restricted and active stages each carry
 * a reason and the required next action.
 * TRACE:TT-R123-01 — an account status control is present on the detail screen.
 */
export function AccountDetailPage() {
  const { accountId = '' } = useParams();
  const resolved = resolveContext();
  const toast = useToast();

  const [shareOpen, setShareOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<CustomerAccountAction | null>(null);

  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const account = useAsync(async () => {
    if (!companyId || !userId) throw new Error('no session');
    return (await getAccount(companyId, userId, accountId)).data;
  }, [companyId, userId, accountId]);

  const ibans = useAsync(async () => {
    if (!accountId) return [];
    return (await getIbansForAccount(accountId)).data;
  }, [accountId]);

  const statusMutation = useMutation(async (action: CustomerAccountAction) => {
    if (!companyId || !userId) throw new Error('no session');
    return (
      await updateAccountStatus(companyId, userId, accountId, action, newIdempotencyKey('acct'))
    ).data;
  });

  const renameMutation = useMutation(async (name: string) => {
    if (!companyId || !userId) throw new Error('no session');
    return (await renameAccount(companyId, userId, accountId, name)).data;
  });

  if (!resolved) return null;

  const ctx = resolved.entitlements;
  const canSeeSensitive = resolveCapability(ctx, CAPABILITIES.accountsViewSensitive).allowed;
  const canManage = resolveCapability(ctx, CAPABILITIES.accountsManage).allowed;

  if (account.status === 'loading') {
    return (
      <>
        <MobileHeader title="Account" backTo="/mobile/accounts" backLabel="Accounts" />
        <LoadingRegion label="Loading account">
          <div className="tm-stack">
            <CardSkeleton height={150} />
            <CardSkeleton height={200} />
          </div>
        </LoadingRegion>
      </>
    );
  }

  if (account.status === 'error') {
    return (
      <>
        <MobileHeader title="Account" backTo="/mobile/accounts" backLabel="Accounts" />
        <ErrorState error={account.error} onRetry={account.reload} title="We could not load this account" />
      </>
    );
  }

  const acct: Account = account.data;
  const reserved = acct.balance - acct.availableBalance;
  const actions = allowedAccountActions(acct);

  return (
    <>
      <MobileHeader
        title={acct.displayName}
        backTo="/mobile/accounts"
        backLabel="Accounts"
        trailing={
          canManage ? (
            <button
              type="button"
              className="tm-btn tm-btn--quiet tm-btn--sm"
              onClick={() => {
                setRenameValue(acct.displayName);
                setRenameError(null);
                setRenameOpen(true);
              }}
            >
              Rename
            </button>
          ) : null
        }
      />

      <div className="tm-stack">
        {/* Status and the required next action. */}
        {acct.status !== 'active' ? (
          <Notice
            tone={acct.status === 'suspended' || acct.status === 'closed' ? 'danger' : 'warning'}
            title={`Account ${acct.status}`}
          >
            {acct.statusReason ?? 'Some activity is limited on this account.'}
            {acct.status === 'restricted' ? (
              <p style={{ margin: '6px 0 0' }}>
                You can still receive money into this account.
              </p>
            ) : null}
            {acct.status === 'suspended' ? (
              <p style={{ margin: '6px 0 0' }}>
                Contact support to discuss reinstating this account.
              </p>
            ) : null}
          </Notice>
        ) : null}

        {/* Balances kept separate: available, booked and reserved. */}
        <div className="tm-card">
          <p className="tm-eyebrow">Available</p>
          <Amount minorUnits={acct.availableBalance} currency={acct.currency} size="hero" />
          <hr className="tm-divider" style={{ margin: '14px 0' }} />
          <DetailList
            items={[
              {
                label: 'Booked balance',
                value: <Amount minorUnits={acct.balance} currency={acct.currency} />,
                hint: 'Including pending items',
              },
              {
                label: 'Reserved',
                value: <Amount minorUnits={reserved} currency={acct.currency} />,
                hint: 'Held for pending transactions',
              },
              { label: 'Currency', value: acct.currency },
              { label: 'Status', value: <StatusBadge status={acct.status} /> },
            ]}
          />
        </div>

        <div className="tm-stack tm-stack--tight">
          <Link
            to={`/mobile/transactions?accountId=${acct.id}`}
            className="tm-btn tm-btn--secondary tm-btn--block"
          >
            View transactions
          </Link>
          <button
            type="button"
            className="tm-btn tm-btn--secondary tm-btn--block"
            onClick={() => setShareOpen(true)}
          >
            Share account details
          </button>
        </div>

        {/* Domestic details first, international second (WR-037). */}
        {canSeeSensitive ? (
          <>
            {acct.localDetails?.sortCode && acct.localDetails.accountNumber ? (
              <section aria-labelledby="acct-domestic">
                <SectionHeader title="UK domestic details" id="acct-domestic" />
                <div className="tm-card tm-stack tm-stack--tight">
                  <CopyField
                    label="Sort code"
                    value={formatSortCode(acct.localDetails.sortCode)}
                    copyValue={acct.localDetails.sortCode}
                  />
                  <CopyField
                    label="Account number"
                    value={acct.localDetails.accountNumber}
                    copyValue={acct.localDetails.accountNumber}
                  />
                  <p className="tm-hint" style={{ margin: 0 }}>
                    Use these for payments from within the UK.
                  </p>
                </div>
              </section>
            ) : null}

            <section aria-labelledby="acct-international">
              <SectionHeader title="International details" id="acct-international" />
              {ibans.status === 'loading' ? (
                <CardSkeleton height={120} />
              ) : (ibans.data ?? []).length === 0 ? (
                <div className="tm-card">
                  <p className="tm-muted" style={{ margin: 0 }}>
                    This account has no IBAN. It can still receive UK domestic payments.
                  </p>
                </div>
              ) : (
                <div className="tm-stack tm-stack--tight">
                  {(ibans.data ?? []).map((iban: Iban) => (
                    <div key={iban.id} className="tm-card tm-stack tm-stack--tight">
                      <div className="tm-between">
                        <strong>{iban.label ?? 'IBAN'}</strong>
                        <span className="tm-inline" style={{ gap: 6 }}>
                          {iban.isVirtual ? (
                            <StatusBadge status="virtual" label="Virtual" tone="info" />
                          ) : null}
                          {iban.status !== 'active' ? <StatusBadge status={iban.status} /> : null}
                        </span>
                      </div>
                      <CopyField label="IBAN" value={formatIban(iban.iban)} copyValue={iban.iban} />
                      {iban.bic ? (
                        <CopyField label="BIC / SWIFT" value={iban.bic} copyValue={iban.bic} />
                      ) : null}
                      <CopyField
                        label="Account holder"
                        value={iban.holderName}
                        copyValue={iban.holderName}
                      />
                      {iban.statusReason ? (
                        <p className="tm-hint" style={{ margin: 0 }}>
                          {iban.statusReason}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : (
          <Notice tone="info" title="Account details are hidden">
            You do not have permission to view full account identifiers. Ask an administrator if you
            need them.
          </Notice>
        )}

        {/* Receiving instructions. */}
        <section aria-labelledby="acct-receiving">
          <SectionHeader title="Getting paid" id="acct-receiving" />
          <div className="tm-card">
            <ol style={{ margin: 0, paddingInlineStart: 18 }} className="tm-muted">
              <li>Share the details above with whoever is paying you.</li>
              <li>UK payers should use the sort code and account number.</li>
              <li>Payers outside the UK should use the IBAN and BIC.</li>
              <li>Ask them to include a reference so you can match the payment.</li>
            </ol>
          </div>
        </section>

        {/* Status control (TT-R123-01). */}
        {canManage && actions.length > 0 ? (
          <section aria-labelledby="acct-controls">
            <SectionHeader title="Account controls" id="acct-controls" />
            <div className="tm-card tm-stack tm-stack--tight">
              {actions.includes('freeze') ? (
                <>
                  <button
                    type="button"
                    className="tm-btn tm-btn--secondary tm-btn--block"
                    onClick={() => setPendingAction('freeze')}
                  >
                    Freeze outgoing payments
                  </button>
                  <p className="tm-hint" style={{ margin: 0 }}>
                    Stops money leaving this account. Incoming payments still arrive.
                  </p>
                </>
              ) : null}
              {actions.includes('unfreeze') ? (
                <button
                  type="button"
                  className="tm-btn tm-btn--block"
                  onClick={() => setPendingAction('unfreeze')}
                >
                  Unfreeze this account
                </button>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>

      {/* Share sheet. */}
      <Sheet open={shareOpen} onClose={() => setShareOpen(false)} title="Share account details">
        <p className="tm-muted">
          This copies the details a payer needs. It does not include your balance.
        </p>
        <button
          type="button"
          className="tm-btn tm-btn--block"
          style={{ marginBlockStart: 12 }}
          onClick={() => {
            const lines = [
              acct.displayName,
              ...(acct.localDetails?.sortCode
                ? [`Sort code: ${formatSortCode(acct.localDetails.sortCode)}`]
                : []),
              ...(acct.localDetails?.accountNumber
                ? [`Account number: ${acct.localDetails.accountNumber}`]
                : []),
              ...(ibans.data ?? []).map((i) => `IBAN: ${formatIban(i.iban)}`),
            ];
            void navigator.clipboard.writeText(lines.join('\n')).then(() => {
              toast.show({ message: 'Account details copied.' });
              setShareOpen(false);
            });
          }}
        >
          Copy details
        </button>
        <button
          type="button"
          className="tm-btn tm-btn--secondary tm-btn--block"
          style={{ marginBlockStart: 8 }}
          onClick={() => setShareOpen(false)}
        >
          Cancel
        </button>
      </Sheet>

      {/* Rename sheet (XS-SMEM-TT-R037-D7). */}
      <Sheet open={renameOpen} onClose={() => setRenameOpen(false)} title="Rename account">
        <div className="tm-field">
          <label className="tm-label" htmlFor="rename-input">
            Account name
          </label>
          <input
            id="rename-input"
            className="tm-input"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            aria-invalid={renameError ? true : undefined}
            aria-describedby={renameError ? 'rename-error' : undefined}
          />
          <div className="tm-field__error-slot">
            {renameError ? (
              <span className="tm-field__error" id="rename-error">
                {renameError}
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="tm-btn tm-btn--block"
          disabled={renameMutation.pending}
          onClick={() => {
            void renameMutation.run(renameValue).then((outcome) => {
              if (outcome.ok) {
                account.setData(outcome.value);
                setRenameOpen(false);
                toast.show({ message: 'Account renamed.' });
              } else if (outcome.error) {
                const err = outcome.error as { fieldErrors?: Record<string, string> };
                setRenameError(err.fieldErrors?.['displayName'] ?? 'Could not rename the account.');
              }
            });
          }}
        >
          {renameMutation.pending ? 'Saving…' : 'Save name'}
        </button>
      </Sheet>

      {/* Status change confirmation (TT-R123-02). */}
      <ConfirmSheet
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        busy={statusMutation.pending}
        onConfirm={() => {
          if (!pendingAction) return;
          void statusMutation.run(pendingAction).then((outcome) => {
            if (outcome.ok) {
              // TRACE:TT-R123-03 — the account reflects the updated status.
              account.setData(outcome.value);
              toast.show({
                message:
                  pendingAction === 'freeze'
                    ? 'Outgoing payments are frozen on this account.'
                    : 'This account is active again.',
              });
            }
            setPendingAction(null);
          });
        }}
        title={pendingAction === 'freeze' ? 'Freeze outgoing payments?' : 'Unfreeze this account?'}
        body={
          pendingAction === 'freeze'
            ? 'Payments out of this account will be stopped until you unfreeze it. Money can still come in. This is recorded in your activity.'
            : 'Payments out of this account will be allowed again. This is recorded in your activity.'
        }
        confirmLabel={pendingAction === 'freeze' ? 'Freeze account' : 'Unfreeze account'}
        {...(pendingAction === 'freeze' ? { tone: 'danger' as const } : {})}
      />
    </>
  );
}
